import * as React from "react";
import { Suspense } from "react";
import { Canvas, useThree, useFrame } from "@react-three/fiber";
import { Float, Loader, Html, OrbitControls } from "@react-three/drei";

import sampleData from "../samples/stamps.json";

const stampKeys = Object.keys(sampleData);
const length = stampKeys.length;

const randomVector = (r: number): [number, number, number] => [
  r / 2 - Math.random() * r,
  r / 2 - Math.random() * r,
  r / 2 - Math.random() * r,
];
const randomEuler = (): [number, number, number] => [
  Math.random() * Math.PI,
  Math.random() * Math.PI,
  Math.random() * Math.PI,
];
const data = Array.from({ length }, () => ({
  random: Math.random(),
  position: randomVector(15),
  rotation: randomEuler(),
}));

interface ViewerPageProps {
  viewOnly?: boolean;
  onSelect?: (id: string) => void;
  onHover?: (id: string | null) => void;
  selectedId?: string;
  filteredKeys?: string[];
}

export function ViewerPage({ viewOnly, onSelect, onHover, selectedId, filteredKeys }: ViewerPageProps) {
  const controlsRef = React.useRef<any>(null);
  const isInteracting = React.useRef(false);

  const selectedPosition: [number, number, number] | null = React.useMemo(() => {
    if (!selectedId) return null;
    const idx = stampKeys.indexOf(selectedId);
    return idx >= 0 ? data[idx].position : null;
  }, [selectedId]);

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <Canvas
        camera={{ position: [-15, 0, 0] }}
        style={{ height: "100%", width: "100%" }}
      >
        <Suspense fallback={null}>
          <Samples viewOnly={viewOnly} onSelect={onSelect} onHover={onHover} selectedId={selectedId} filteredKeys={filteredKeys} data={data} />
          {viewOnly ? (
            <OrbitControls
              ref={controlsRef}
              enableZoom={false}
              enablePan={false}
              enableRotate={false}
              autoRotate={true}
              autoRotateSpeed={1}
            />
          ) : (
            <OrbitControls
              ref={controlsRef}
              enablePan={false}
              minDistance={5}
              maxDistance={50}
              autoRotate={true}
              autoRotateSpeed={0.05}
              onStart={() => { isInteracting.current = true; }}
              onEnd={() => { isInteracting.current = false; }}
            />
          )}
          <CameraController
            selectedPosition={selectedPosition}
            controlsRef={controlsRef}
            isInteracting={isInteracting}
          />
        </Suspense>
        <ambientLight />
      </Canvas>
      <Loader />
    </div>
  );
}

interface CameraControllerProps {
  selectedPosition: [number, number, number] | null;
  controlsRef: React.MutableRefObject<any>;
  isInteracting: React.MutableRefObject<boolean>;
}

function CameraController({ selectedPosition, controlsRef, isInteracting }: CameraControllerProps) {
  const { camera } = useThree();

  // Run at priority 1 so this executes after OrbitControls' own useFrame (priority 0)
  useFrame(() => {
    if (isInteracting.current) return;
    const controls = controlsRef.current;
    if (!controls) return;

    if (selectedPosition) {
      const [sx, sy, sz] = selectedPosition;

      // Lerp the orbit target toward the selected stamp
      controls.target.x += (sx - controls.target.x) * 0.04;
      controls.target.y += (sy - controls.target.y) * 0.04;
      controls.target.z += (sz - controls.target.z) * 0.04;

      // Desired camera position: radially outward from cloud centre through the stamp
      const len = Math.sqrt(sx * sx + sy * sy + sz * sz);
      if (len > 0.001) {
        const DIST = 9; // distance behind stamp (outside cloud)
        const nx = sx / len, ny = sy / len, nz = sz / len;
        const desiredX = sx + nx * DIST;
        const desiredY = sy + ny * DIST;
        const desiredZ = sz + nz * DIST;
        camera.position.x += (desiredX - camera.position.x) * 0.04;
        camera.position.y += (desiredY - camera.position.y) * 0.04;
        camera.position.z += (desiredZ - camera.position.z) * 0.04;
      }
      controls.update();
    } else {
      // Ease target back to origin
      controls.target.x += (0 - controls.target.x) * 0.03;
      controls.target.y += (0 - controls.target.y) * 0.03;
      controls.target.z += (0 - controls.target.z) * 0.03;
      controls.update();
    }
  }, 1);

  return null;
}

interface SamplesProps {
  viewOnly?: boolean;
  onSelect?: (id: string) => void;
  onHover?: (id: string | null) => void;
  selectedId?: string;
  filteredKeys?: string[];
  data: Array<{ random: number; position: [number, number, number]; rotation: [number, number, number] }>;
}

function Samples({ viewOnly, onSelect, onHover, selectedId, filteredKeys, data }: SamplesProps) {
  const hasSelection = !!selectedId;
  return (
    <group position={[0, 0, 0]}>
      {data.map((props, key) => {
        const isSelected = stampKeys[key] === selectedId;
        return (
          <Float key={key} rotationIntensity={isSelected ? 0 : 1} floatIntensity={1}>
            <ImageSample
              viewOnly={viewOnly}
              onSelect={onSelect}
              onHover={onHover}
              isSelected={isSelected}
              hasSelection={hasSelection}
              isFiltered={filteredKeys ? filteredKeys.includes(stampKeys[key]) : true}
              {...props}
              index={key}
            />
          </Float>
        );
      })}
    </group>
  );
}

interface ImageSampleProps {
  random: number;
  position: [number, number, number];
  rotation: [number, number, number];
  index: number;
  viewOnly?: boolean;
  isSelected?: boolean;
  hasSelection?: boolean;
  isFiltered?: boolean;
  onSelect?: (id: string) => void;
  onHover?: (id: string | null) => void;
}

function ImageSample({ random, viewOnly, onSelect, onHover, isSelected, hasSelection, isFiltered = true, position, rotation: initialRotation, index: indexProp }: ImageSampleProps) {
  const index = stampKeys[indexProp];
  const file = `https://vidyagiri.com/stamps/img/${index}.png`;
  const data = (sampleData as Record<string, any>)[index];
  const [hovered, setHovered] = React.useState(false);
  const groupRef = React.useRef<any>(null);

  const handleClick = React.useCallback(() => {
    if (onSelect) onSelect(index);
  }, [onSelect, index]);

  const handleMouseEnter = React.useCallback(() => {
    setHovered(true);
    if (onHover) onHover(index);
  }, [onHover, index]);

  const handleMouseLeave = React.useCallback(() => {
    setHovered(false);
    if (onHover) onHover(null);
  }, [onHover]);

  // Smoothly lerp rotation to [0,0,0] when selected, back to initial when not
  useFrame(() => {
    if (!groupRef.current) return;
    const r = groupRef.current.rotation;
    const [tx, ty, tz] = isSelected ? [0, 0, 0] : initialRotation;
    const factor = isSelected ? 0.08 : 0.04;
    r.x += (tx - r.x) * factor;
    r.y += (ty - r.y) * factor;
    r.z += (tz - r.z) * factor;
  });

  const baseOpacity = isSelected ? 1 : !isFiltered ? 0.12 : hasSelection ? 0.35 : 0.75;
  const imgStyle: React.CSSProperties = {
    cursor: onSelect ? "pointer" : "default",
    transition: "transform 300ms ease, opacity 300ms ease",
    transform: isSelected ? "scale(1.12)" : "scale(1)",
    filter: "none",
    opacity: hovered ? 1 : baseOpacity,
    display: "block",
  };

  return (
    <group ref={groupRef} position={position}>
      <Html transform>
        {viewOnly ? (
          <img src={file} alt={"stamp " + index} width={100} draggable={false} style={{ display: "block", userSelect: "none" }} />
        ) : onSelect ? (
          <img
            src={file}
            alt={"stamp " + index}
            width={100}
            draggable={false}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className="viewer"
            style={{ ...imgStyle, userSelect: "none" }}
          />
        ) : (
          <a href={"../stamp/" + index}>
            <img className="viewer" src={file} alt={"stamp " + index} width={100} draggable={false} style={{ cursor: "pointer", display: "block", userSelect: "none" }} />
          </a>
        )}
      </Html>
    </group>
  );
}
